
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SetupStep } from "@/components/SetupProgressTracker";
import { useState, useEffect } from "react";

// This hook provides reactive setup progress tracking based on actual configuration status
export function useSetupProgress() {
  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
  });
  
  // State for steps
  const [setupSteps, setSetupSteps] = useState<SetupStep[]>([
    {
      id: "apns",
      name: "Configure Apple\nPush Notification Service",
      completed: false,
      path: "/settings?category=system",
    },
    {
      id: "abm",
      name: "Setup Apple\nBusiness Manager",
      completed: false,
      path: "/settings?category=system",
    },
    {
      id: "profiles",
      name: "Create\nConfiguration Profiles",
      completed: false,
      path: "/profiles",
    },
    {
      id: "devices",
      name: "Enroll\nDevices",
      completed: false,
      path: "/devices",
    },
  ]);

  // Check for APNS and ABM configurations in localStorage
  useEffect(() => {
    // Skip if not logged in
    if (!session) return;

    // Get configurations from localStorage
    const apnsConfigs = localStorage.getItem('patching_apns_configs');
    const abmConfigs = localStorage.getItem('patching_abm_configs');
    const parsedApnsConfigs = apnsConfigs ? JSON.parse(apnsConfigs) : [];
    const parsedAbmConfigs = abmConfigs ? JSON.parse(abmConfigs) : [];

    // Check for device enrollments
    const deviceData = localStorage.getItem('enrolled_devices');
    const enrolledDevices = deviceData ? JSON.parse(deviceData) : [];

    // Check for configuration profiles
    const profilesData = localStorage.getItem('configuration_profiles');
    const configProfiles = profilesData ? JSON.parse(profilesData) : [];

    // Update steps based on configuration status
    setSetupSteps(prev => prev.map(step => {
      if (step.id === 'apns') {
        return { ...step, completed: parsedApnsConfigs.length > 0 };
      }
      if (step.id === 'abm') {
        return { ...step, completed: parsedAbmConfigs.length > 0 };
      }
      if (step.id === 'profiles') {
        return { ...step, completed: configProfiles.length > 0 };
      }
      if (step.id === 'devices') {
        return { ...step, completed: enrolledDevices.length > 0 };
      }
      return step;
    }));
  }, [session]);

  // Function to update the completion status of a step
  const updateStepCompletion = (stepId: string, completed: boolean) => {
    setSetupSteps(prev => 
      prev.map(step => step.id === stepId ? { ...step, completed } : step)
    );
  };

  return {
    setupSteps,
    updateStepCompletion,
    isLoading: isSessionLoading,
    isLoggedIn: !!session,
  };
}
